import { Credentials } from '@quadminds/client-apis/auth';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

import { BrandedLoginForm, ClassicLoginForm } from './LoginForms';
import {
    applyAutofill,
    handleLoginSubmitError,
    handleThirdPartyLoginError,
    LoginErrorDeps,
    runSafely,
} from './loginHandlers';
import { acceptsSaasCode, isBrandedPlatform } from '../../utils';

type LoginComponentProps = {
    isLoading: boolean;
    handleLoginWithGoogle: () => Promise<string | undefined>;
    handleLoginWithMicrosoft: () => Promise<string | undefined>;
    handleLoginWithApple: () => Promise<string | undefined>;
    handleLogin: (values: Credentials) => Promise<string | undefined>;
    isSaas: boolean;
};

const LoginComponent: React.FC<LoginComponentProps> = ({
    isLoading,
    handleLoginWithGoogle,
    handleLoginWithMicrosoft,
    handleLoginWithApple,
    handleLogin,
    isSaas,
}) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [firebaseError, setFirebaseError] = useState<string>('');
    const [legacyLoginError, setLegacyLoginError] = useState<string>('');

    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    const loginSchema = Yup.object().shape({
        email: Yup.string().email(t('login.validEmail')).required(t('login.email')),
        password: Yup.string().min(4, t('login.validPassword')).required(t('login.putPassword')),
    });

    const saasLoginSchema = Yup.object().shape({
        // In saas, this field may be the email or the user's code (such us 'nsa@operaciones')
        email: Yup.string().required(t('login.putCodeOrEmail')),
        password: Yup.string().min(4, t('login.validPassword')).required(t('login.putPassword')),
    });

    const errorDeps: LoginErrorDeps = { navigate, setFirebaseError, setLegacyLoginError, t };

    const formik = useFormik({
        initialValues: { email: '', password: '' },
        validationSchema: acceptsSaasCode ? saasLoginSchema : loginSchema,
        onSubmit: (values: Credentials) =>
            runSafely(
                () => handleLogin(values),
                (error) => handleLoginSubmitError(error, errorDeps),
            ),
    });

    const onThirdPartyError = (error: unknown) => handleThirdPartyLoginError(error, errorDeps);

    const handleGoogleLogin = () => runSafely(handleLoginWithGoogle, onThirdPartyError);
    const handleMicrosoftLogin = () => runSafely(handleLoginWithMicrosoft, onThirdPartyError);
    const handleAppleLogin = () => runSafely(handleLoginWithApple, onThirdPartyError);

    // The inputs use decoy name attributes, so each field is set explicitly instead of by name
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('email', e.target.value);
        if (!e.target.value) {
            formik.setFieldValue('password', '');
            formik.setFieldTouched('email', false);
            formik.setFieldTouched('password', false);
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('password', e.target.value);
    };

    const handleEmailBlur = () => {
        formik.setFieldTouched('email', true);
    };

    const handlePasswordBlur = () => {
        formik.setFieldTouched('password', true);
    };

    const handleAutofill = (event: React.AnimationEvent<HTMLInputElement>) => applyAutofill(event, formik);

    const togglePassword = () => {
        setShowPassword(!showPassword);
    };

    // Clean up error message
    useEffect(() => {
        firebaseError &&
            setTimeout(() => {
                setFirebaseError('');
            }, 10000);
    }, [firebaseError]);

    useEffect(() => {
        if (searchParams.get('logout') === 'true') {
            formik.resetForm();
            setFirebaseError('');
            setLegacyLoginError('');
            setShowPassword(false);
        }
        // formik ref is stable — intentionally excluded to avoid infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        // Some browsers inject stored login values after the first paint.
        // Resetting once on mount keeps the form blank while still allowing password save prompts.
        formik.resetForm();
        // formik is stable for the lifetime of the component here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isBrandedPlatform) {
        return (
            <BrandedLoginForm
                formik={formik}
                isLoading={isLoading}
                firebaseError={firebaseError}
                legacyLoginError={legacyLoginError}
                showPassword={showPassword}
                onTogglePassword={togglePassword}
                onEmailChange={handleEmailChange}
                onPasswordChange={handlePasswordChange}
                onEmailBlur={handleEmailBlur}
                onPasswordBlur={handlePasswordBlur}
                onAutofill={handleAutofill}
                onGoogleLogin={handleGoogleLogin}
                onMicrosoftLogin={handleMicrosoftLogin}
                onAppleLogin={handleAppleLogin}
            />
        );
    }

    return (
        <ClassicLoginForm
            formik={formik}
            isLoading={isLoading}
            firebaseError={firebaseError}
            legacyLoginError={legacyLoginError}
            showPassword={showPassword}
            onTogglePassword={togglePassword}
            onEmailChange={handleEmailChange}
            onPasswordChange={handlePasswordChange}
            onEmailBlur={handleEmailBlur}
            onPasswordBlur={handlePasswordBlur}
            onAutofill={handleAutofill}
            onGoogleLogin={handleGoogleLogin}
            isSaas={isSaas}
        />
    );
};

export default LoginComponent;
